import {
  DecoratedSilo,
  getCollateralAssets,
  getEffectiveCollateral,
  getNonCollateralAssets,
  SiloAsset,
} from 'app/queries/decoratedSilo';
import { flow } from 'fp-ts/lib/function';
import { cropLTV } from './cropLTV';
import { normalizeArgs } from './normalizeArgs';
import { performCalculations } from './performCalculations';
import { CalculateLTVParams, CollateralAsset, DebtAsset, LTV } from './types';
import { ChecksumAddress } from '@silo/types';

export const baseCalculateLTV = flow(normalizeArgs, performCalculations, cropLTV);

export const calculateLTV = (args: CalculateLTVParams): LTV => {
  const ltv = baseCalculateLTV(args);

  return {
    ...ltv,
    args,
    collateralAssetsMap: createAssetMap(ltv.collateralAssets),
    debtAssetsMap: createAssetMap(ltv.debtAssets),
  };
};

const createAssetMap = <T extends { assetAddress: ChecksumAddress }>(assets: T[]): Record<ChecksumAddress, T> =>
  assets.reduce(
    (acc, asset) => ({
      ...acc,
      [asset.assetAddress]: asset,
    }),
    {}
  );

export const calculateLTVFromDecoratedSilo = (decoratedSilo: DecoratedSilo): LTV | null => {
  const collateralAssets = getCollateralAssets(decoratedSilo);
  const debtAssets = getNonCollateralAssets(decoratedSilo);

  if (!collateralAssets.length || !debtAssets.length) return null;

  return calculateLTV({
    collateralAssets: collateralAssets.map(mapToCollateralAsset),
    debtAssets: debtAssets.map(mapToDebtAsset),
  });
};

export const mapToCollateralAsset = (asset: SiloAsset): CollateralAsset => {
  const { assetAddress, decimals, priceInNative, maximumLTV, liquidationThreshold } = asset;
  const balance = getEffectiveCollateral(asset);

  return {
    assetAddress,
    decimals,
    balance: balance.toBigInt(),
    price: priceInNative.toBigInt(),
    maximumLTV: maximumLTV.toBigInt(),
    liquidationThreshold: liquidationThreshold.toBigInt(),
  };
};

export const mapToDebtAsset = (asset: SiloAsset): DebtAsset => {
  const { assetAddress, decimals, priceInNative, debtBalance } = asset;

  return {
    assetAddress,
    decimals,
    balance: debtBalance.toBigInt(),
    price: priceInNative.toBigInt(),
  };
};
